import React from "react";
import {Image, Platform, StatusBar, Text, TouchableOpacity, View} from 'react-native';
import {LinearGradient} from "expo-linear-gradient";
import * as LocalAuthentication from 'expo-local-authentication';
import {getAccessToken} from "../helpers/storage";
import LogoImage from "../../assets/logo-small.png";
import * as SplashScreen from "expo-splash-screen";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {useTranslation} from 'react-i18next';
import '../../i18n';
import {getSetting} from "../helpers/persistentSettings";
import {getRouteForAppMode} from '../config/appVariant';
import {useTheme} from '../helpers/ThemeContext';
import {useThemedStyles} from '../helpers/useThemedStyles';

let tryCount = 0;

const StartScreen = ({ navigation, route }) => {
    const { t } = useTranslation();
    const { theme, isDark } = useTheme();
    const styles = useThemedStyles(buildStyles);
    const [logout, setLogout] = React.useState(true);
    const [turnOff, setTurnOff] = React.useState(true);
    const [isDayOpen, setIsDayOpen] = React.useState(undefined);
    const [token, setToken] = React.useState(undefined);
    const { successDayClose } = route?.params || {};
    const logStartScreen = (...args) => {
        console.log('[StartScreen]', ...args);
    };

    const navigateAfterAuth = React.useCallback(async () => {
        const userAppMode = await getSetting('userAppMode');
        const targetRoute = getRouteForAppMode(userAppMode, isDayOpen);
        logStartScreen('navigateAfterAuth', { targetRoute, isDayOpen });
        navigation.reset({
            index: 0,
            routes: [{ name: targetRoute, params: { loggedIn: true } }],
        });
    }, [isDayOpen, navigation]);

    const readDayOpenState = async () => {
        const storedDayOpened = await AsyncStorage.getItem('dayOpened');
        return storedDayOpened === 'true';
    };

    React.useEffect(() => {
        const checkLogin = async () => {
            const getToken = await getAccessToken();
            logStartScreen('Initial checkLogin start', { hasToken: !!getToken, successDayClose });
            if (getToken) {
                setTurnOff(false);
                setToken(getToken);
                setLogout(false);
            }
            const dayOpen = await readDayOpenState();
            setIsDayOpen(dayOpen);
            logStartScreen('Initial checkLogin done', { hasToken: !!getToken, dayOpen });

            try {
                await SplashScreen.hideAsync();
            } catch (_) {
                // hideAsync can reject/throw if the native module is unavailable
                // or the splash is already hidden — never let it break checkLogin.
            }
        };
        checkLogin();
    }, []);

    React.useEffect(() => {
        const checkLogin = async () => {
            const getToken = await getAccessToken();
            logStartScreen('Refresh checkLogin start', { hasToken: !!getToken, successDayClose });
            if (getToken) {
                setTurnOff(false);
                setToken(getToken);
                setLogout(false);
            }
            const dayOpen = await readDayOpenState();
            setIsDayOpen(dayOpen);
            logStartScreen('Refresh checkLogin done', { hasToken: !!getToken, dayOpen });

            try {
                await SplashScreen.hideAsync();
            } catch (_) {
                // hideAsync can reject/throw if the native module is unavailable
                // or the splash is already hidden — never let it break checkLogin.
            }
        };
        checkLogin();
    }, [navigation, successDayClose]);

    const authenticate = async () => {
        logStartScreen('authenticate called', { hasToken: !!token, isDayOpen, platform: Platform.OS });
        if (!token) return; // Only authenticate if the user is logged in
        if (Platform.OS === "web") return navigateAfterAuth();
        try {
            tryCount++;
            const hasHardware = await LocalAuthentication.hasHardwareAsync();
            logStartScreen('local auth hardware', { hasHardware });
            if (!hasHardware) {
                return navigateAfterAuth();
            }

            const isEnrolled = await LocalAuthentication.isEnrolledAsync();
            logStartScreen('local auth enrolled', { isEnrolled });
            if (!isEnrolled) {
                return navigateAfterAuth();
            }

            const result = await LocalAuthentication.authenticateAsync({
                promptMessage: t("authenticate_prompt"),
                fallbackLabel: t("authenticate_fallback")
            });

            if (result.success) {
                logStartScreen('local auth success, navigating forward', { isDayOpen });
                return navigateAfterAuth();
            }

            // Avoid recursive re-prompts on cancel/failure; let user retry from UI.
            logStartScreen('local auth cancelled/failed', result);

        } catch (error) {
            logStartScreen('local auth error, fallback navigation', error?.message || error);
            return navigateAfterAuth();
        }
    };

    React.useEffect(() => {
        logStartScreen('auto-auth gate changed', { logout, hasToken: !!token, isDayOpen });
        if (!logout && token && isDayOpen !== undefined) {
            globalThis.setTimeout(async () => {
                logStartScreen('auto-auth timeout fired');
                await authenticate();
            }, 300);
        }
    }, [authenticate, logout, token, isDayOpen]);

    return (
        <LinearGradient
            colors={['#f2e2d2', '#e6d6c1', '#dcd5ce']}  // Example gradient colors
            style={styles.background}
        >
            <StatusBar backgroundColor="transparent" translucent barStyle={isDark ? 'light-content' : 'dark-content'} />
            <View style={styles.container}>
                {Platform.OS !== "web" && <Image
                    source={LogoImage}
                    style={[styles.logo, { width: 150, height: 150 }]}
                />}
                {Platform.OS === "web" && <img
                    alt={"Happy Solutions Logo"}
                    src={'https://happy-releases.fra1.cdn.digitaloceanspaces.com/Uploads/logo.png'}
                    style={styles.logo}
                />}
                <Text style={styles.title}>{t("welcome")}</Text>
                <Text style={styles.description}>
                    {t("start_subtitle")}
                </Text>
                <TouchableOpacity style={styles.loginButton}
                                  onPress={() => logout ? navigation.navigate("LoginScreen") : authenticate()}>
                    <Text style={styles.loginButtonText}>{t("get_started")}</Text>
                </TouchableOpacity>
            </View>
        </LinearGradient>
    );
};

const buildStyles = (theme) => ({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    logo: {
        width: 150,
        height: 150,
        marginBottom: 80,
        backgroundColor: 'transparent'
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center'
    },
    description: {
        fontSize: 16,
        textAlign: 'center',
        marginBottom: 20,
    },
    loginButton: {
        backgroundColor: theme.colors.primary,
        paddingVertical: 15,
        paddingHorizontal: 30,
        borderRadius: 5,
    },
    loginButtonText: {
        fontSize: 24,
        color: "white",
        paddingHorizontal: 20,
        textAlign: 'center',
    },
    background: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});

export default StartScreen;